import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Lock, Download, Users, CheckCircle, TrendingUp, BarChart3, ArrowLeft, Clock, MousePointerClick } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const [passcode, setPasscode] = useState("");
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const handleVerify = useCallback(async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, { passcode });
      setVerified(true);
      const res = await axios.get(`${API}/admin/summary`, { params: { passcode } });
      setSummary(res.data);
    } catch { toast.error("Invalid passcode"); } finally { setLoading(false); }
  }, [passcode]);

  const handleExport = useCallback(() => {
    window.open(`${API}/admin/export?passcode=${encodeURIComponent(passcode)}`, "_blank");
  }, [passcode]);

  useEffect(() => {
    if (verified) {
      const interval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/admin/summary`, { params: { passcode } });
          setSummary(res.data);
        } catch {}
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [verified, passcode]);

  if (!verified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card data-testid="admin-passcode-form" className="w-full max-w-sm">
            <CardHeader className="text-center">
              <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mx-auto mb-3">
                <Lock className="w-5 h-5 text-primary" />
              </div>
              <CardTitle className="font-display text-lg">Decision Layer Admin</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Enter the pilot passcode.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input data-testid="admin-passcode-input" type="password" placeholder="Passcode" value={passcode}
                onChange={e => setPasscode(e.target.value)} onKeyDown={e => e.key === "Enter" && handleVerify()} />
              <Button data-testid="admin-unlock-button" onClick={handleVerify} disabled={loading || !passcode} className="w-full rounded-[12px]">
                {loading ? "Verifying..." : "Unlock"}
              </Button>
              <Button variant="ghost" onClick={() => navigate("/")} className="w-full rounded-[12px] gap-2 text-sm">
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="gap-1"><ArrowLeft className="w-4 h-4" /> Home</Button>
            <span className="font-display text-sm font-semibold text-foreground">Decision Layer Analytics</span>
          </div>
          <Button variant="secondary" size="sm" onClick={handleExport} className="rounded-[12px] gap-2">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {summary ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Metric icon={<Users className="w-4 h-4" />} label="Sessions Started" value={summary.total_started} />
              <Metric icon={<CheckCircle className="w-4 h-4" />} label="Completed" value={summary.total_completed} />
              <Metric icon={<BarChart3 className="w-4 h-4" />} label="Completion Rate" value={`${summary.completion_rate}%`} />
              <Metric icon={<TrendingUp className="w-4 h-4" />} label="Avg Clarity Uplift" value={summary.avg_clarity_uplift > 0 ? `+${summary.avg_clarity_uplift}` : summary.avg_clarity_uplift} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Metric icon={<Clock className="w-4 h-4" />} label="Avg Time (s)" value={summary.avg_time_to_complete || 0} />
              <Metric icon={<MousePointerClick className="w-4 h-4" />} label="Educrib Clicks" value={summary.deep_link_clicks} />
              <Metric icon={<BarChart3 className="w-4 h-4" />} label="Click-through" value={`${summary.click_through_rate}%`} />
            </div>

            <Separator />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <h3 className="font-display text-sm font-semibold text-foreground mb-4">Top Topics</h3>
                {summary.top_topics?.length > 0 ? (
                  <div className="space-y-2">
                    {summary.top_topics.map((t, i) => (
                      <div key={i} className="flex items-center justify-between bg-card border border-border rounded-lg p-3">
                        <span className="text-sm">{t.topic}</span>
                        <span className="text-sm font-medium text-primary">{t.count}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">No data yet.</p>}
              </div>
              <div>
                <h3 className="font-display text-sm font-semibold text-foreground mb-4">Top Constraints</h3>
                {summary.top_constraints?.length > 0 ? (
                  <div className="space-y-2">
                    {summary.top_constraints.map((c, i) => (
                      <div key={i} className="flex items-center justify-between bg-card border border-border rounded-lg p-3">
                        <span className="text-sm">{c.constraint}</span>
                        <span className="text-sm font-medium text-primary">{c.count}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">No data yet.</p>}
              </div>
            </div>

            <div>
              <h3 className="font-display text-sm font-semibold text-foreground mb-4">Readiness Distribution</h3>
              {summary.readiness_distribution?.length > 0 ? (
                <div className="space-y-2">
                  {summary.readiness_distribution.map((r, i) => (
                    <div key={i} className="flex items-center justify-between bg-card border border-border rounded-lg p-3">
                      <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${
                        r.band === 'high' ? 'badge-ready' : r.band === 'medium' ? 'badge-emerging' : 'badge-exploratory'
                      }`}>{r.band}</span>
                      <span className="text-sm font-medium text-primary">{r.count}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No data yet.</p>}
            </div>
          </motion.div>
        ) : <p className="text-center text-sm text-muted-foreground py-12">Loading...</p>}
      </div>
    </div>
  );
}

function Metric({ icon, label, value }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center gap-2 text-muted-foreground mb-2">{icon}<span className="text-xs">{label}</span></div>
        <p className="text-2xl font-display font-semibold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}
